import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import { mediaDims } from "@/lib/media-dims";
import { responsiveSources } from "@/lib/responsive-images";
import { prefersReducedMotion } from "@/lib/motion";

type PassThrough = Omit<
  ComponentPropsWithoutRef<"img">,
  "src" | "alt" | "className" | "sizes" | "loading" | "width" | "height"
>;

interface MediaProps extends PassThrough {
  src: string;
  alt: string;
  className?: string;
  eager?: boolean;
  /** Layout hint for responsive selection; defaults to a card-width heuristic. */
  sizes?: string;
  /**
   * Poster frame for video sources. Shown before playback and used as the
   * permanent static fallback when motion is reduced or autoplay is withheld.
   */
  poster?: string;
}

/**
 * Renders an <img> for still assets and a muted looping <video> for .mp4
 * assets (used in place of heavy animated GIFs). Still images are served
 * through <picture> with AVIF + WebP srcsets so the browser downloads the
 * smallest acceptable variant. Explicit intrinsic width/height are always set
 * to avoid layout shift. Videos only start downloading near the viewport.
 *
 * Video safeguards:
 *  - always muted (never autoplay audio) and `playsInline`;
 *  - never autoplays when the reader prefers reduced motion, or on small
 *    screens, or on a metered/slow connection — the poster frame stands in;
 *  - `controls` are exposed whenever autoplay is withheld so the reader can
 *    still choose to watch it.
 */
export const Media = forwardRef<HTMLElement, MediaProps>(
  ({ src, alt, className, eager, sizes, poster, ...rest }, ref) => {
    const { width, height } = mediaDims(src);
    const isVideo = src.endsWith(".mp4");
    const videoRef = useRef<HTMLVideoElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [inView, setInView] = useState(!!eager);
    const [mayAutoplay, setMayAutoplay] = useState(true);

    useImperativeHandle(ref, () => (isVideo ? videoRef.current : imgRef.current) as HTMLElement);

    // Decide once, on the client, whether this video is allowed to move.
    useEffect(() => {
      if (!isVideo) return;
      const smallScreen =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 767px)").matches;
      const conn = (
        navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
      ).connection;
      const frugal =
        conn?.saveData === true || (conn?.effectiveType ?? "").includes("2g");
      setMayAutoplay(!prefersReducedMotion() && !smallScreen && !frugal);
    }, [isVideo]);

    useEffect(() => {
      if (!isVideo || inView) return;
      const el = videoRef.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setInView(true);
            observer.disconnect();
          }
        },
        { rootMargin: "300px" },
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, [isVideo, inView]);

    useEffect(() => {
      if (inView && mayAutoplay) videoRef.current?.play().catch(() => undefined);
    }, [inView, mayAutoplay]);

    if (isVideo) {
      // Load the source when near the viewport, or immediately if the reader
      // has controls and might press play.
      const shouldLoad = inView || !mayAutoplay;
      return (
        <video
          {...(rest as unknown as ComponentPropsWithoutRef<"video">)}
          ref={videoRef}
          src={shouldLoad ? src : undefined}
          poster={poster}
          aria-label={alt}
          className={className}
          width={width}
          height={height}
          loop={mayAutoplay}
          muted
          playsInline
          controls={!mayAutoplay}
          preload={mayAutoplay ? "none" : "metadata"}
        />
      );
    }


    const { avif, webp } = responsiveSources(src);
    const sizesAttr =
      sizes ?? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 720px";
    const hasVariants = Boolean(avif || webp);

    const img = (
      <img
        {...rest}
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        sizes={hasVariants ? sizesAttr : undefined}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        /* React 18 does not map the camelCase form onto the DOM attribute, so
           it must be written lowercase to avoid an unknown-prop warning. */
        {...{ fetchpriority: eager ? "high" : "auto" }}
      />
    );


    if (!hasVariants) return img;

    return (
      <picture className="contents">
        {avif && <source type="image/avif" srcSet={avif} sizes={sizesAttr} />}
        {webp && <source type="image/webp" srcSet={webp} sizes={sizesAttr} />}
        {img}
      </picture>
    );
  },
);

Media.displayName = "Media";
